// useEnvironmentBaseline — hook driving the Layer 1 room-scan wizard.
//
// Talks to the 5 backend endpoints in app.py:
//   GET   /api/baseline/config              — which sub-features are enabled
//   GET   /api/baseline/status/<test_id>    — current state (for resume)
//   POST  /api/baseline/start               — initialise / reset the row
//   POST  /api/baseline/upload-frame        — multipart: one frame blob
//   POST  /api/baseline/finalize            — run Rekognition + set verdict
//
// Wizard status lifecycle (server-authoritative for verdict; the wizard
// UI derives in-progress states locally):
//
//   'loading'   — initial fetch
//   'skipped'   — feature flags off → resolve immediately
//   'ready'     — config loaded, scan not started yet
//   'scanning'  — frames currently being captured
//   'analyzing' — frames done, waiting for /finalize response
//   'passed'    — verdict 'passed' from server
//   'failed'    — verdict 'failed' from server
//   'error'     — network failure
//
// All network calls return { ok, ...data, error? } so the caller
// renders errors without try/catch.

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const ENDPOINTS = {
  config:        '/api/baseline/config',
  status:        (testId) => `/api/baseline/status/${encodeURIComponent(testId)}`,
  start:         '/api/baseline/start',
  uploadFrame:   '/api/baseline/upload-frame',
  finalize:      '/api/baseline/finalize',
};


export function useEnvironmentBaseline(testId, {
  applicationId = null,
  candidateEmail = null,
} = {}) {
  const [config, setConfig] = useState(null);
  const [row, setRow] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // ── Pull config + current row on mount ─────────────────────────────────
  const refresh = useCallback(async () => {
    if (!testId) return;
    try {
      const [configRes, statusRes] = await Promise.all([
        axios.get(ENDPOINTS.config),
        axios.get(ENDPOINTS.status(testId)),
      ]);
      setConfig(configRes.data);

      // If none of the Layer 1 sub-features are enabled, the gate is a
      // no-op — surface 'skipped' so the wizard renders nothing.
      const anyEnabled =
        configRes.data.room_scan ||
        configRes.data.object_catalog ||
        configRes.data.background_hash;
      if (!anyEnabled) {
        setStatus('skipped');
        setRow(null);
        return;
      }

      const nextRow = statusRes.data?.exists ? statusRes.data : null;
      setRow(nextRow);
      // Map server verdict to wizard status. 'pending' on the server
      // means: scan never finalised — wizard treats as 'ready' so the
      // candidate can scan now.
      const verdict = nextRow?.verdict || 'pending';
      if (verdict === 'passed') setStatus('passed');
      else if (verdict === 'failed') setStatus('failed');
      else setStatus('ready');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[baseline] refresh failed:', err?.message || err);
      setError(err?.message || 'Failed to load baseline status');
      setStatus('error');
    }
  }, [testId]);

  useEffect(() => { void refresh(); }, [refresh]);

  // ── Initialise / reset the baseline row server-side ────────────────────
  const start = useCallback(async () => {
    if (!testId) return { ok: false, error: 'missing test_id' };
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(ENDPOINTS.start, {
        test_id: testId,
        application_id: applicationId,
        candidate_email: candidateEmail,
      }, { timeout: 10000 });
      setStatus('scanning');
      return { ok: true, ...res.data };
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'start failed';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setBusy(false);
    }
  }, [testId, applicationId, candidateEmail]);

  // ── Upload a single frame (Blob from the canvas) ───────────────────────
  // Fire-and-forget by design — the parent component dispatches uploads
  // without awaiting so the capture cadence isn't blocked by a slow link.
  // Each upload returns its own result for optional inspection.
  const uploadFrame = useCallback(async (blob, frameIndex) => {
    if (!testId || !blob) return { ok: false, error: 'missing data' };
    try {
      const fd = new FormData();
      fd.append('test_id', testId);
      fd.append('frame_index', String(frameIndex));
      fd.append('image', blob, `frame-${frameIndex}.jpg`);
      const res = await axios.post(ENDPOINTS.uploadFrame, fd, { timeout: 20000 });
      return { ok: true, ...res.data };
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'upload failed';
      return { ok: false, error: msg };
    }
  }, [testId]);

  // ── Tell the server to run Rekognition + set the verdict ───────────────
  const finalize = useCallback(async (sweepDurationSeconds) => {
    if (!testId) return { ok: false, error: 'missing test_id' };
    setBusy(true);
    setError(null);
    setStatus('analyzing');
    try {
      const res = await axios.post(ENDPOINTS.finalize, {
        test_id: testId,
        sweep_duration_seconds: sweepDurationSeconds,
      }, { timeout: 60000 });   // Rekognition fan-out can take 10+ seconds
      await refresh();
      return { ok: true, ...res.data };
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'finalize failed';
      setError(msg);
      setStatus('error');
      return { ok: false, error: msg };
    } finally {
      setBusy(false);
    }
  }, [testId, refresh]);

  // ── Reset to ready state for a retry ───────────────────────────────────
  const reset = useCallback(() => {
    setError(null);
    setStatus('ready');
  }, []);

  return {
    config, row, status, error, busy,
    refresh, start, uploadFrame, finalize, reset,
  };
}
